import { useState, useCallback, useMemo } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  CustomerPortalService,
  CustomerPortalSession,
  CustomerPortalSessionInput,
  SubscriptionUpdateInput,
  SubscriptionCancelInput,
  SubscriptionCancel,
  SubscriptionHistoryItem,
  SubscriptionHistoryFilter,
  Pagination,
  SortItem,
} from "@/utils";

export interface UseCustomerPortalReturn {
  // Data state
  session: CustomerPortalSession | null;
  subscriptionHistory: SubscriptionHistoryItem[];
  cancelResult: SubscriptionCancel | null;
  loading: boolean;
  loadingHistory: boolean;
  loadingCancel: boolean;
  loadingUpdate: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Pagination
  currentPage: number;
  pageSize: number;

  // Core operations
  createSession: (
    params: CustomerPortalSessionInput
  ) => Promise<CustomerPortalSession | null>;
  updateSubscription: (
    params: SubscriptionUpdateInput
  ) => Promise<CustomerPortalSession | null>;
  cancelSubscription: (
    params: SubscriptionCancelInput
  ) => Promise<SubscriptionCancel | null>;
  loadSubscriptionHistory: (
    filter?: SubscriptionHistoryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
    reset?: boolean
  ) => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  refreshHistory: () => Promise<void>;

  // Filter operations
  setHistoryFilters: (filters: SubscriptionHistoryFilter) => void;

  // Error handling
  lastError: string | null;
  historyError: string | null;
  cancelError: string | null;
  updateError: string | null;
  clearError: () => void;
  clearHistoryError: () => void;
  clearCancelError: () => void;
  clearUpdateError: () => void;

  // Helper operations
  reset: () => void;
  resetHistory: () => void;

  // Computed values
  historyById: (id: string) => SubscriptionHistoryItem | undefined;
}

const DEFAULT_PAGE_SIZE = 20;

export const useCustomerPortal = (): UseCustomerPortalReturn => {
  // Data state
  const [session, setSession] = useState<CustomerPortalSession | null>(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState<
    SubscriptionHistoryItem[]
  >([]);
  const [cancelResult, setCancelResult] = useState<SubscriptionCancel | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filters
  const [historyFilters, setHistoryFilters] =
    useState<SubscriptionHistoryFilter>({});

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => subscriptionHistory.length === 0 && !loadingHistory,
    [subscriptionHistory.length, loadingHistory]
  );

  const historyById = useCallback(
    (id: string) => subscriptionHistory.find((item) => item.id === id),
    [subscriptionHistory]
  );

  // Clear error functions
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const clearHistoryError = useCallback(() => {
    setHistoryError(null);
  }, []);

  const clearCancelError = useCallback(() => {
    setCancelError(null);
  }, []);

  const clearUpdateError = useCallback(() => {
    setUpdateError(null);
  }, []);

  // Reset functions
  const reset = useCallback(() => {
    setSession(null);
    setSubscriptionHistory([]);
    setCancelResult(null);
    setLastError(null);
    setHistoryError(null);
    setCancelError(null);
    setUpdateError(null);
    setLoading(false);
    setLoadingHistory(false);
    setLoadingCancel(false);
    setLoadingUpdate(false);
    setHasMore(false);
    setCurrentPage(1);
    setHistoryFilters({});
  }, []);

  const resetHistory = useCallback(() => {
    setSubscriptionHistory([]);
    setHistoryError(null);
    setLoadingHistory(false);
    setHasMore(false);
    setCurrentPage(1);
  }, []);

  // Create customer portal session
  const createSession = useCallback(
    async (
      params: CustomerPortalSessionInput
    ): Promise<CustomerPortalSession | null> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await CustomerPortalService.createSession(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const sessionData = result.data[0] as CustomerPortalSession;
          setSession(sessionData);
          setLastError(null);
          return sessionData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create customer portal session";
          setLastError(errorMessage);
          setSession(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create customer portal session";
        setLastError(errorMessage);
        setSession(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Update subscription
  const updateSubscription = useCallback(
    async (
      params: SubscriptionUpdateInput
    ): Promise<CustomerPortalSession | null> => {
      setLoadingUpdate(true);
      setUpdateError(null);

      try {
        const result = await CustomerPortalService.updateSubscription(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const sessionData = result.data[0] as CustomerPortalSession;
          setSession(sessionData);
          setUpdateError(null);
          return sessionData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update subscription";
          setUpdateError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update subscription";
        setUpdateError(errorMessage);
        return null;
      } finally {
        setLoadingUpdate(false);
      }
    },
    []
  );

  // Cancel subscription
  const cancelSubscription = useCallback(
    async (
      params: SubscriptionCancelInput
    ): Promise<SubscriptionCancel | null> => {
      setLoadingCancel(true);
      setCancelError(null);

      try {
        const result = await CustomerPortalService.cancelSubscription(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const cancelData = result.data[0] as SubscriptionCancel;
          setCancelResult(cancelData);
          setCancelError(null);
          return cancelData;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to cancel subscription";
          setCancelError(errorMessage);
          setCancelResult(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to cancel subscription";
        setCancelError(errorMessage);
        setCancelResult(null);
        return null;
      } finally {
        setLoadingCancel(false);
      }
    },
    []
  );

  // Load subscription history
  const loadSubscriptionHistory = useCallback(
    async (
      filter: SubscriptionHistoryFilter = {},
      pagination?: Pagination,
      sortBy?: SortItem[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSize;
      const isAppending = !reset && targetPage > 1;

      if (isAppending) {
        // Don't set loading state when appending, use hasMore logic instead
      } else {
        setLoadingHistory(true);
        if (reset) {
          setSubscriptionHistory([]);
          setCurrentPage(1);
        }
      }

      // Update internal filters
      setHistoryFilters(filter);

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await CustomerPortalService.getSubscriptionHistory(
          filter,
          paginationParams,
          sortBy
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newHistory = result.data as SubscriptionHistoryItem[];

          if (isAppending) {
            setSubscriptionHistory((prev) => [...prev, ...newHistory]);
          } else {
            setSubscriptionHistory(newHistory);
          }

          setHasMore(newHistory.length === targetPageSize);
          setCurrentPage(targetPage);
          setHistoryError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load subscription history";
          setHistoryError(errorMessage);
          if (!isAppending) setSubscriptionHistory([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load subscription history";
        setHistoryError(errorMessage);
        if (!isAppending) setSubscriptionHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    },
    [pageSize]
  );

  // Load more history
  const loadMoreHistory = useCallback(async () => {
    if (!loadingHistory && hasMore) {
      const nextPage = currentPage + 1;
      await loadSubscriptionHistory(
        historyFilters,
        { page: nextPage, pageSize },
        undefined,
        false
      );
    }
  }, [
    loadSubscriptionHistory,
    loadingHistory,
    hasMore,
    currentPage,
    pageSize,
    historyFilters,
  ]);

  // Refresh history
  const refreshHistory = useCallback(async () => {
    await loadSubscriptionHistory(
      historyFilters,
      { page: 1, pageSize },
      undefined,
      true
    );
  }, [loadSubscriptionHistory, historyFilters, pageSize]);

  return {
    // Data state
    session,
    subscriptionHistory,
    cancelResult,
    loading,
    loadingHistory,
    loadingCancel,
    loadingUpdate,
    hasMore,
    isEmpty,

    // Pagination
    currentPage,
    pageSize,

    // Core operations
    createSession,
    updateSubscription,
    cancelSubscription,
    loadSubscriptionHistory,
    loadMoreHistory,
    refreshHistory,

    // Filter operations
    setHistoryFilters,

    // Error handling
    lastError,
    historyError,
    cancelError,
    updateError,
    clearError,
    clearHistoryError,
    clearCancelError,
    clearUpdateError,

    // Helper operations
    reset,
    resetHistory,

    // Computed values
    historyById,
  };
};
